import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Copy, Link2, ShieldQuestion } from "lucide-react";

export default function SettingsPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => setS(data)).catch(() => {});
  }, []);

  const save = async () => {
    setError(""); setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        business_name: s.business_name, public_slug: s.public_slug,
        routing_mode: s.routing_mode, backup_number: s.backup_number,
        dedup_hours: Number(s.dedup_hours), prefill_message: s.prefill_message,
      });
      setS(data);
      toast.success("Configuración guardada");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  if (!s) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-[#25D366] animate-spin" /></div>;

  const publicUrl = `${window.location.origin}/${s.public_slug || "ir"}`;
  const copy = () => { navigator.clipboard.writeText(publicUrl); toast.success("Enlace copiado"); };

  return (
    <div className="max-w-3xl">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-50 mb-1">Configuración</h1>
      <p className="text-slate-400 text-sm mb-6">Reparto, enlace público y número de respaldo</p>

      <div className="bg-[#111822] border border-[#25D36633] rounded-xl p-5 mb-4">
        <div className="flex items-center gap-2 mb-3"><Link2 className="w-5 h-5 text-[#25D366]" /><span className="text-xs font-mono-lf uppercase tracking-widest text-[#25D366]">Tu enlace público</span></div>
        <div className="flex items-center gap-2">
          <code data-testid="public-link-value" className="flex-1 bg-[#0B0F14] border border-[#1E2D3D] rounded-lg px-3 py-2.5 text-slate-200 font-mono-lf text-sm truncate">{publicUrl}</code>
          <Button data-testid="copy-link-button" onClick={copy} size="icon" className="bg-[#25D366] hover:bg-[#22BF5B] text-[#0B0F14] shrink-0"><Copy className="w-4 h-4" /></Button>
        </div>
        <p className="text-xs text-slate-500 mt-2">Pega este enlace en tus anuncios. Cada clic asigna un vendedor y abre WhatsApp.</p>
      </div>

      <div className="bg-[#111822] border border-[#1E2D3D] rounded-xl p-5 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label className="text-slate-300 mb-1.5 block">Nombre del negocio</Label>
            <Input data-testid="settings-business-name" value={s.business_name || ""} onChange={(e) => setS({ ...s, business_name: e.target.value })} className="bg-[#0B0F14] border-[#1E2D3D]" /></div>
          <div><Label className="text-slate-300 mb-1.5 block">Slug del enlace (ej. ir)</Label>
            <Input data-testid="settings-slug" value={s.public_slug || ""} onChange={(e) => setS({ ...s, public_slug: e.target.value.replace(/[^a-z0-9-]/gi, "") })} className="bg-[#0B0F14] border-[#1E2D3D] font-mono-lf" /></div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label className="text-slate-300 mb-1.5 block">Modo de reparto</Label>
            <Select value={s.routing_mode} onValueChange={(v) => setS({ ...s, routing_mode: v })}>
              <SelectTrigger data-testid="settings-routing-mode" className="bg-[#0B0F14] border-[#1E2D3D] text-slate-100"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111822] border-[#1E2D3D] text-slate-100">
                <SelectItem value="round_robin">Round-robin (turnos)</SelectItem>
                <SelectItem value="random">Aleatorio</SelectItem>
              </SelectContent>
            </Select></div>
          <div><Label className="text-slate-300 mb-1.5 block">Reconexión mismo vendedor (horas)</Label>
            <Input data-testid="settings-dedup-hours" type="number" value={s.dedup_hours} onChange={(e) => setS({ ...s, dedup_hours: e.target.value })} className="bg-[#0B0F14] border-[#1E2D3D] font-mono-lf" /></div>
        </div>

        <div>
          <Label className="text-slate-300 mb-1.5 flex items-center gap-2"><ShieldQuestion className="w-4 h-4 text-amber-400" /> Número de respaldo (cuando nadie disponible)</Label>
          <Input data-testid="settings-backup-number" value={s.backup_number || ""} onChange={(e) => setS({ ...s, backup_number: e.target.value })} placeholder="+593 99 000 0000" className="bg-[#0B0F14] border-[#1E2D3D] font-mono-lf" />
          <p className="text-xs text-slate-500 mt-1">Con código de país. Si lo dejas vacío y nadie está disponible, el cliente verá un aviso.</p>
        </div>

        <div><Label className="text-slate-300 mb-1.5 block">Mensaje prellenado de WhatsApp</Label>
          <Textarea data-testid="settings-prefill-message" value={s.prefill_message || ""} onChange={(e) => setS({ ...s, prefill_message: e.target.value })} rows={2} className="bg-[#0B0F14] border-[#1E2D3D] text-slate-100" /></div>

        {error && <p className="text-sm text-red-400">{error}</p>}
        <Button data-testid="settings-save-button" onClick={save} disabled={saving} className="bg-[#25D366] hover:bg-[#22BF5B] text-[#0B0F14] font-semibold">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar configuración"}
        </Button>
      </div>
    </div>
  );
}
